import {
  type SatelliteWebLandlordGetScreeningCriterionDto,
  type SatelliteWebLandlordSetScreeningCriterionDto,
  ScreeningCriteriaType,
} from '@zg-rentals/ts-satellite-client';
import { NOT_APPLICABLE } from './constants';

/** Get a single criterion by type from the criteria array. */
export function getCriterion(
  criteria: Array<SatelliteWebLandlordGetScreeningCriterionDto>,
  type: ScreeningCriteriaType,
) {
  return criteria.find((criterion) => criterion.type === type);
}

/** Build form state from saved criteria (income, credit, pet policy). */
export function buildInitialFormState(criteria: Array<SatelliteWebLandlordGetScreeningCriterionDto>) {
  const income = getCriterion(criteria, ScreeningCriteriaType.INCOME_TO_RENT_RATIO);
  const credit = getCriterion(criteria, ScreeningCriteriaType.CREDIT_SCORE_MIN);
  const catCount = getCriterion(criteria, ScreeningCriteriaType.PET_POLICY_CAT_COUNT);
  const smallDog = getCriterion(criteria, ScreeningCriteriaType.PET_POLICY_SMALL_DOG_COUNT);
  const largeDog = getCriterion(criteria, ScreeningCriteriaType.PET_POLICY_LARGE_DOG_COUNT);
  const petDeposit = getCriterion(criteria, ScreeningCriteriaType.PET_POLICY_PET_DEPOSIT);
  const petRent = getCriterion(criteria, ScreeningCriteriaType.PET_POLICY_PET_RENT);

  const isPetsAllowed =
    Number(catCount?.criterion ?? 0) > 0 ||
    Number(smallDog?.criterion ?? 0) > 0 ||
    Number(largeDog?.criterion ?? 0) > 0;

  const petCriteria = [catCount, smallDog, largeDog, petDeposit, petRent];
  const isPetPolicyNegotiable = petCriteria.some((criterion) => criterion?.negotiable === true);

  return {
    incomeToRent: income?.criterion ?? NOT_APPLICABLE,
    isIncomeToRentNegotiable: income?.negotiable ?? false,
    creditScore: credit?.criterion ?? NOT_APPLICABLE,
    isCreditScoreNegotiable: credit?.negotiable ?? false,
    isPetsAllowed,
    isPetPolicyNegotiable,
    petCatCount: catCount?.criterion ?? '0',
    petSmallDogCount: smallDog?.criterion ?? '0',
    petLargeDogCount: largeDog?.criterion ?? '0',
    petDeposit: petDeposit?.criterion ?? '',
    petRent: petRent?.criterion ?? '',
  };
}

export type FormState = ReturnType<typeof buildInitialFormState>;

type EnabledCriteria = {
  isIncomeFieldEnabled: boolean;
  isCreditFieldEnabled: boolean;
};

/** Build API payload from current form state for criteria set. Only includes criteria that are enabled for the listing. */
export function buildCriteriaPayload(
  formState: FormState,
  enabledCriteria: EnabledCriteria,
): Array<SatelliteWebLandlordSetScreeningCriterionDto> {
  return [
    ...(enabledCriteria.isIncomeFieldEnabled
      ? [
          {
            type: ScreeningCriteriaType.INCOME_TO_RENT_RATIO,
            criterion: formState.incomeToRent,
            negotiable: formState.isIncomeToRentNegotiable,
          },
        ]
      : []),
    ...(enabledCriteria.isCreditFieldEnabled
      ? [
          {
            type: ScreeningCriteriaType.CREDIT_SCORE_MIN,
            criterion: formState.creditScore === '' ? NOT_APPLICABLE : formState.creditScore,
            negotiable: formState.isCreditScoreNegotiable,
          },
        ]
      : []),
    // "Pets not allowed" is encoded as zero counts: the backend derives allowed-ness from
    // count > 0, so sending stale counts here would leave pets enabled after the user picks "No".
    {
      type: ScreeningCriteriaType.PET_POLICY_CAT_COUNT,
      criterion: formState.isPetsAllowed ? formState.petCatCount : '0',
      negotiable: formState.isPetPolicyNegotiable,
    },
    {
      type: ScreeningCriteriaType.PET_POLICY_SMALL_DOG_COUNT,
      criterion: formState.isPetsAllowed ? formState.petSmallDogCount : '0',
      negotiable: formState.isPetPolicyNegotiable,
    },
    {
      type: ScreeningCriteriaType.PET_POLICY_LARGE_DOG_COUNT,
      criterion: formState.isPetsAllowed ? formState.petLargeDogCount : '0',
      negotiable: formState.isPetPolicyNegotiable,
    },
    {
      type: ScreeningCriteriaType.PET_POLICY_PET_DEPOSIT,
      criterion: formState.isPetsAllowed ? formState.petDeposit : '',
      negotiable: formState.isPetPolicyNegotiable,
    },
    {
      type: ScreeningCriteriaType.PET_POLICY_PET_RENT,
      criterion: formState.isPetsAllowed ? formState.petRent : '',
      negotiable: formState.isPetPolicyNegotiable,
    },
  ];
}
